"use client";
import type {
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import {
  CheckCircle,
  Eye,
  Loader2,
  Plus,
  Save,
  Trash,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import {
  useVersionAwareFiles,
  useVersionAwareObservations,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import {
  createObservation,
  deleteObservation,
  fetchObservations,
  updateObservation,
} from "../../../redux/slices/observationSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { sharedObservationsProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { OfflineImage } from "../../shared/offline-image";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../ui/accordion.jsx";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent } from "../../ui/card.jsx";
import { Label } from "../../ui/label.jsx";
import { LoadingIndicator } from "../../ui/loading-indicator";
import {
  OptimizedCheckbox,
  OptimizedInput,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "./image-capture-picker";
import { LocalSectionImportControl } from "./local-section-import-control";

// Predefined observation titles (optional - can be used as suggestions)

interface SiteObservationsProps {
  siteId: string;
  templateId: string;
  reportId: string;
  readOnly?: boolean;
  isViewingHistoricalVersion?: boolean;
  versionData?: any;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  previousCompletedReports?: any[];
}

interface ObservationImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface ObservationItem {
  id: string;
  title: string;
  status: "ok" | "not-ok" | "n-a" | "pending";
  comments: string;
  description: string;
  images: ObservationImage[];
  marksAsPunchListItem: boolean;
  observationDate: string;
}

interface ObservationsData {
  observations: ObservationItem[];
  summary: string;
}

const initialObservations: ObservationsData = {
  observations: [],
  summary: "",
};

export function SiteObservationsSimple({
  siteId,
  templateId,
  reportId,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: SiteObservationsProps) {
  const { notify } = useNotification();

  const [formData, setFormData] = useState(
    initialObservations as ObservationsData,
  );
  const formDataRef = useLatest(formData);
  const [observationData, setObservationData] = useState({} as any);
  const [selectedImage, setSelectedImage] = useState(
    null as ObservationImage | null,
  );

  const [uploadingObservationImages, setUploadingObservationImages] = useState<
    Record<string, boolean>
  >({});
  const [observationProgressData, setObservationProgressData] = useState([]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [newlyAddedId, setNewlyAddedId] = useState<string | null>(null);

  const dispatch = useAppDispatch();

  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);
  const currentSiteData = useVersionAwareSiteData(readOnly);
  const currentObservationData = useVersionAwareObservations(readOnly);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power).
  const draftKey =
    !readOnly && reportId
      ? `report:${reportId}:site-observations:${templateId}`
      : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  // Restore any unsaved draft once on mount, before server data can
  // overwrite it below.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once local form state matches the confirmed server record, the
  // IndexedDB draft is redundant — clear it so a future mount doesn't
  // restore stale local data over a fresher server record.
  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  // Update progress in parent component

  useEffect(() => {
    const observationsProgress = sharedObservationsProgress(
      observationProgressData,
      formData?.summary || "",
      files,
    );

    setSessionProgress((prev: any) => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        "overall-site-observations": observationsProgress,
      },
    }));
  }, [
    formData,
    templateId,
    observationProgressData,
    files,
    setSessionProgress,
  ]);

  // Fetch data on mount
  useEffect(() => {
    const isOffline =
      typeof navigator !== "undefined" && navigator.onLine === false;

    if (!isOffline) {
      dispatch(
        fetchObservations({
          siteId,
          templateId,
          reportId,
          // observationType: "general",
        }) as any,
      );
      dispatch(fetchFiles({ siteId, reportId }) as any);
    }
  }, [dispatch, siteId, templateId, reportId]);

  // When observations are loaded or new ones are added
  useEffect(() => {
    if (currentObservationData) {
      const observationIds =
        currentObservationData
          ?.filter((obs: any) => obs?.observationType === "general")
          ?.map((obs: any) => obs.id || obs._id) || [];

      // If there's a newly added observation, expand only that one
      if (newlyAddedId) {
        setExpandedItems([newlyAddedId]);
        setNewlyAddedId(null);
      } else {
        // Otherwise, expand all observations (initial load)
        setExpandedItems(observationIds);
      }
    }
  }, [currentObservationData, newlyAddedId]);

  // Keep newly imported (not-yet-saved) observations expanded too, so they
  // are visible as soon as they land in observationProgressData.
  useEffect(() => {
    const pendingImportedIds = (observationProgressData || [])
      .filter((obs: any) => obs?._sourceId && !obs?._id)
      .map((obs: any) => obs.id);

    if (pendingImportedIds.length > 0) {
      setExpandedItems((prev) => [
        ...prev,
        ...pendingImportedIds.filter((id: string) => !prev.includes(id)),
      ]);
    }
  }, [observationProgressData]);
  const handleSaveObservation = useCallback(
    async (data: any) => {
      try {
        if (!data?.observationId || !data?.observationData) return;

        await requireMutationSuccess(
          dispatch(
            updateObservation({
              observationId: data.observationId,
              observationData: {
                ...data.observationData,
                siteId,
                templateId,
                reportId,
              },
            }) as any,
          ),
        );
        const config = notificationHelpers.formSaveSuccess("Observation");
        notify(config.message, config.type, config.options);
      } catch (error) {
        console.error("[ObservationSave] Error saving observation:", error);
        const config = notificationHelpers.formSaveError(
          "Observation",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, notify, reportId, siteId, templateId],
  );

  const handleSaveForm = useCallback(
    async (data: ObservationsData) => {
      try {
        const mutationResult1 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: {
                overallSiteObservations: data?.summary || "",
              },
            } as any),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Observations Summary",
          [mutationResult1],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        console.error("[ObservationSummarySave] Error saving summary:", error);
        const config = notificationHelpers.formSaveError(
          "Observations Summary",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, siteId, notify],
  );

  const observationSave = useDebouncedSave(observationData, {
    delay: 5000,
    onSave: handleSaveObservation,
    onError: (error) => console.error("[ObservationAutoSave] Error:", error),
  });

  // Imported observations are staged locally in observationProgressData (so
  // they render immediately, just like every other section) and
  // are only persisted to the database once the Save button is clicked (or
  // the debounced auto-save fires) via handleSavePendingImportedObservations.
  const pendingImportedObservations = useMemo(
    () =>
      (observationProgressData || []).filter(
        (obs: any) => obs?._sourceId && !obs?._id,
      ),
    [observationProgressData],
  );

  const handleSavePendingImportedObservations = useCallback(
    async (pending: any[]) => {
      if (!templateId) {
        notify(
          "The audit template is still loading. Please wait and try again.",
          "error",
        );
        return;
      }

      const newlyImported = (pending || []).filter(
        (obs: any) => obs?._sourceId && !obs?._id,
      );

      if (newlyImported.length === 0) return;

      try {
        const mutationResult2 = await Promise.all(
          newlyImported.map((obs: any) => {
            const { id, _importedLocalId, _sourceId, ...rest } = obs;
            return dispatch(
              createObservation({
                ...rest,
                siteId,
                templateId,
                importedFrom: _sourceId,
                observationType: obs?.observationType || "general",
              }) as any,
            ).unwrap();
          }),
        );
        dispatch(
          fetchObservations({
            siteId,
            templateId,
            reportId,
            // observationType: "general",
          }) as any,
        );
        const config = notificationHelpers.formSaveSuccess(
          "Imported Observations",
          [mutationResult2],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Observation",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        console.error(
          "[ObservationImport] Error importing observations:",
          error,
        );
        throw error;
      }
    },
    [templateId, notify, dispatch, siteId, reportId],
  );

  const importedObservationsSave = useDebouncedSave(
    pendingImportedObservations,
    {
      delay: 5000,
      onSave: handleSavePendingImportedObservations,
      onError: (error) =>
        console.error("[ObservationImportAutoSave] Error:", error),
    },
  );

  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 5000,
    onSave: handleSaveForm,
    onError: (error) =>
      console.error("[ObservationSummaryAutoSave] Error:", error),
  });

  useEffect(() => {
    if (currentSiteData) {
      const observationsSummary =
        currentSiteData?.overallSiteObservations || "";

      const newFormData = {
        ...formDataRef.current,
        summary: observationsSummary,
      };
      resetFormBaseline(newFormData);
      setFormData(newFormData);
    }
  }, [currentSiteData, resetFormBaseline, formDataRef]);

  // Observation CRUD updates its list independently of the unsaved summary.
  useEffect(() => {
    setObservationProgressData(
      currentObservationData?.filter(
        (obs) => obs?.observationType === "general",
      ) || [],
    );
  }, [currentObservationData]);

  // Save handler for image descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult3 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult3],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for image descriptions
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving =
    isFormSaving ||
    observationSave.isSaving ||
    importedObservationsSave.isSaving ||
    isImageSaving;
  const hasUnsavedChanges =
    hasFormUnsavedChanges ||
    observationSave.hasUnsavedChanges ||
    importedObservationsSave.hasUnsavedChanges ||
    hasImageUnsavedChanges;
  const saveError =
    formSaveError ||
    observationSave.saveError ||
    importedObservationsSave.saveError ||
    imageSaveError;
  const lastSaved = useMemo(() => {
    if (!lastSavedForm) return lastSavedImage;
    if (!lastSavedImage) return lastSavedForm;
    if (!observationSave.lastSaved) return observationSave.lastSaved;
    return lastSavedForm > lastSavedImage ? lastSavedForm : lastSavedImage;
  }, [lastSavedForm, lastSavedImage, observationSave.lastSaved]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
    observationSave.forceSave();
    importedObservationsSave.forceSave();
  }, [
    forceFormSave,
    forceImageSave,
    observationSave,
    importedObservationsSave,
  ]);

  // Get loading status for UI display
  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  const updateObservationItem = (
    itemId: string,
    field: keyof ObservationItem,
    value: string | boolean | ObservationImage[],
  ) => {
    setObservationProgressData((prev) =>
      prev.map((observation) =>
        observation.id === itemId || observation._id === itemId
          ? {
              ...observation,
              [field]: value,
            }
          : observation,
      ),
    );
    setObservationData((prev: any) => ({
      ...prev,
      observationId: itemId,
      observationData:
        itemId === prev?.observationId
          ? { ...prev.observationData, [field]: value }
          : { [field]: value },
    }));
  };

  const addObservation = () => {
    if (!templateId) {
      notify(
        "The audit template is still loading. Please wait and try again.",
        "error",
      );
      return;
    }

    const newItem = {
      siteId: siteId,
      templateId: templateId,
      reportId: reportId,
      observationType: "general",
    };
    requireMutationSuccess(dispatch(createObservation(newItem) as any))
      .then((result: any) => {
        const config = notificationHelpers.actionCreate("Observation", result);
        notify(config.message, config.type, config.options);

        // Get the newly created observation ID
        const newObservationId = result?.payload?.id || result?.payload?._id;
        if (newObservationId) {
          // Set this as the newly added ID - this will trigger the useEffect to expand only this one
          setNewlyAddedId(newObservationId);
        }
      })
      .catch((error) => {
        notify(errorMessage(error), "error", { title: "Changes not saved" });
      });
  };

  const deleteObservationItem = (itemId: string) => {
    requireMutationSuccess(dispatch(deleteObservation(itemId) as any))
      .then((mutationOutcome) => {
        const config = notificationHelpers.actionDelete(
          "Observation",
          mutationOutcome,
        );
        notify(config.message, config.type, config.options);
        // Remove from expanded items
        setExpandedItems((prev) => prev.filter((id) => id !== itemId));
      })
      .catch((error) => {
        notify(errorMessage(error), "error", { title: "Changes not saved" });
      });
  };

  const removeImage = (itemId: string, imageId: string) => {
    requireMutationSuccess(dispatch(deleteFile(imageId) as any))
      .then((mutationOutcome) => {
        const config = notificationHelpers.imageDeleteSuccess(
          "",
          mutationOutcome,
        );
        notify(config.message, config.type, config.options);
      })
      .catch((error) => {
        const config = notificationHelpers.imageDeleteError(
          "",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
      });
  };

  const handlePhotoUpload = (
    itemId: string,
    files: FileList | File[] | null,
  ) => {
    if (!files) return;

    setUploadingObservationImages((prev) => ({ ...prev, [itemId]: true }));
    const fileArray = Array.from(files);
    requireMutationSuccess(
      dispatch(
        uploadMultipleFiles({
          files: fileArray,
          metadata: {
            siteId,
            entityType: "observation",
            entityId: itemId,
            reportId,
          },
          filterData: { itemId: true },
        }),
      ),
    )
      .then((mutationOutcome) => {
        fileArray.forEach((file) => {
          const config = notificationHelpers.imageUploadSuccess(
            file.name,
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        });
      })
      .catch((error) => {
        const config = notificationHelpers.imageUploadError(
          fileArray.length === 1
            ? fileArray[0].name
            : `${fileArray.length} files`,
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
      })
      .finally(() => {
        setUploadingObservationImages((prev) => ({ ...prev, [itemId]: false }));
      });
  };

  const updateImageDescription = (
    itemId: string,
    imageId: string,
    description: string,
  ) => {
    setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // Toggle accordion item
  const toggleAccordion = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  // Render observation item
  const renderObservationItem = (observation: any, index: number) => {
    if (!observation) return null;
    const observationId = observation?.id || observation?._id;
    if (!observationId) return null;

    const activeObservation =
      observationData?.observationId === observationId
        ? observationData?.observationData || observation
        : observation;
    const observationFiles = files?.filter(
      (file: any) =>
        String(file?.relatedTo?.entityId || "") === String(observationId),
    );

    // Files loaded after a refresh commonly have `_id`, while files created
    // during the current session commonly have `id`. Keep one stable local
    // identity for rendering and update/delete actions in both cases.
    const getImageId = (image: any) =>
      image?.id || image?._id || image?._clientId;

    return (
      <AccordionItem key={observationId} value={observationId}>
        <AccordionTrigger
          className="hover:no-underline"
          onClick={() => toggleAccordion(observationId)}
        >
          <div className="flex items-center justify-between w-full mr-4">
            <div className="flex items-center gap-3">
              <span className="font-medium">
                #{index + 1}{" "}
                {activeObservation?.title || `Observation ${index + 1}`}
              </span>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 h-6 w-6 p-0"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    deleteObservationItem(observationId);
                  }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="pt-4">
          <div className="space-y-4">
            {/* Observation Title - Text input */}
            <div className="space-y-2">
              <OptimizedInput
                id={`title-${observationId}`}
                label="Observation Title"
                value={activeObservation?.title || ""}
                readOnly={readOnly}
                onChange={(value: any) =>
                  updateObservationItem(observationId, "title", value)
                }
                placeholder="Enter observation title..."
                required
              />
            </div>

            {/* Description (Main input field) */}
            <div className="space-y-2">
              <OptimizedTextarea
                label="Description"
                id={`description-${observationId}`}
                value={activeObservation?.description || ""}
                readOnly={readOnly}
                onChange={(value: any) =>
                  updateObservationItem(observationId, "description", value)
                }
                placeholder="Describe the observation in detail..."
                rows={4}
                required
              />
            </div>

            {/* Comments */}
            <OptimizedTextarea
              label="Comments"
              id={`comments-${observationId}`}
              value={activeObservation?.comments || ""}
              onChange={(e: any) =>
                updateObservationItem(observationId, "comments", e)
              }
              placeholder="Any additional comments or notes..."
              rows={2}
              readOnly={readOnly}
              className="resize-none"
              required
            />

            {/* Punch List Checkbox */}
            <OptimizedCheckbox
              id={`mark-as-punch-${observationId}`}
              label="Mark as a Punch List Item"
              checked={activeObservation?.marksAsPunchListItem || false}
              onChange={(checked: any) =>
                updateObservationItem(
                  observationId,
                  "marksAsPunchListItem",
                  checked as boolean,
                )
              }
              readOnly={readOnly}
            />

            {/* Photos Section */}
            <div className="space-y-2">
              <Label>Photos</Label>
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <ImageCapturePicker
                    buttonText="Upload Photos"
                    isUploading={
                      uploadingObservationImages[observationId] || false
                    }
                    disabled={false}
                    onConfirm={(selectedFiles) =>
                      handlePhotoUpload(observationId, selectedFiles)
                    }
                  />
                )}

                <span className="text-sm text-slate-500 dark:text-muted-foreground">
                  {observationFiles.length} photo
                  {observationFiles.length !== 1 ? "s" : ""} uploaded
                </span>
              </div>

              {uploadingObservationImages[observationId] && (
                <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 py-6 text-sm text-slate-600 dark:bg-muted dark:text-muted-foreground dark:border-border">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Uploading photos...
                </div>
              )}

              {observationFiles.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {observationFiles.map((image: any) => {
                    const imageId = getImageId(image);
                    if (!imageId) return null;

                    return (
                      <div key={imageId} className="relative group">
                        <div className="aspect-video bg-slate-100 rounded overflow-hidden dark:bg-muted">
                          <OfflineImage
                            fileId={imageId}
                            src={image.fileUrl}
                            alt={`Observation ${index + 1} - Photo`}
                            className="w-full h-full object-cover rounded border"
                          />
                        </div>
                        <div className="absolute top-1 right-1 flex gap-1">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity
                                bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                            onClick={() =>
                              setSelectedImage({ ...image, id: imageId })
                            }
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          {!readOnly && (
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity
                                    bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                              onClick={() =>
                                removeImage(observationId, imageId)
                              }
                            >
                              <Trash className="w-3 h-3" />
                            </Button>
                          )}
                        </div>

                        <div className="mt-1 space-y-1">
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-medium text-slate-700 truncate dark:text-foreground">
                              {image.originalName}
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {formatFileSize(image.fileSize)}
                            </Badge>
                          </div>
                          <OptimizedTextarea
                            id={`image-description-${imageId}`}
                            value={
                              imageDescriptions[imageId] ??
                              image.fileDescription ??
                              ""
                            }
                            readOnly={readOnly}
                            onChange={(value) =>
                              updateImageDescription(
                                observationId,
                                imageId,
                                value,
                              )
                            }
                            placeholder="Describe this image..."
                            rows={1}
                          />
                          <div className="text-xs text-slate-500 dark:text-muted-foreground">
                            Date:{" "}
                            {new Date(image.uploadDate).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="overall-site-observations"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          observationProgressData={observationProgressData}
          setObservationProgressData={setObservationProgressData}
        />
      )}
      {/* Floating save section with tooltip */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
                bg-white/95 backdrop-blur-sm
                rounded-full border-2 border-gray-200/80
                shadow-lg
                w-12 h-12 sm:w-16 sm:h-16
                flex items-center justify-center
                group relative
                transition-all duration-300 ease-out
                transform hover:scale-110
                cursor-default
                p-2
              "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
                relative rounded-full w-12 h-12 sm:w-16 sm:h-16
                transition-all duration-300 ease-out
                flex flex-col items-center justify-center
                backdrop-blur-sm border-2
                shadow-lg hover:shadow-xl
                transform hover:scale-110 active:scale-95
                group
                ${
                  hasUnsavedChanges && !readOnly
                    ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
                    : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
                }
                ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
              `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Add Observation Button */}
      {!readOnly && (
        <div className="flex justify-end">
          <Button onClick={addObservation} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Observation
          </Button>
        </div>
      )}

      {/* Observations List */}
      <Card>
        <CardContent className="pt-6">
          {observationProgressData?.filter(
            (obs) => obs?.observationType === "general",
          )?.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 dark:text-muted-foreground">No observations added yet</p>
              <p className="text-sm text-slate-500 mb-4 dark:text-muted-foreground">
                Click "Add Observation" to start recording site observations
              </p>
              <Button onClick={addObservation} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Your First Observation
              </Button>
            </div>
          ) : (
            <Accordion
              type="multiple"
              className="w-full"
              value={expandedItems}
              onValueChange={setExpandedItems}
            >
              {observationProgressData
                ?.filter((obs) => obs?.observationType === "general")
                ?.map((observation, index) =>
                  renderObservationItem(observation, index),
                )}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Observations Summary */}
      <Card>
        {/* <CardHeader>
          <CardTitle>Observations Summary</CardTitle>
        </CardHeader> */}
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <OptimizedTextarea
              label="Overall Summary"
              id="summary"
              value={formData.summary}
              onChange={(e) => setFormData((prev) => ({ ...prev, summary: e }))}
              placeholder="Provide an overall summary of all observations..."
              rows={4}
              readOnly={readOnly}
            />
          </div>
        </CardContent>
      </Card>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={
                  selectedImage?._id ||
                  selectedImage?.id ||
                  selectedImage?._clientId
                }
                src={selectedImage?.fileUrl}
                alt={selectedImage.originalName}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                {new Date(selectedImage.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Footer */}
      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
            Observations Progress:
          </span>
          <Badge variant="outline">
            {sharedObservationsProgress(
              observationProgressData?.filter(
                (obs) => obs?.observationType === "general",
              ) || [],
              formData?.summary || "",
              files,
            )}
            % Complete
          </Badge>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
}
